import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/marketplace.css';

const Marketplace = () => {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');

  // Mock data for initial development
  useEffect(() => {
    // This would be replaced with actual API calls in production
    const mockDatasets = [
      {
        id: 1,
        name: 'MNIST Dataset',
        description: 'Handwritten digits dataset for image classification. Contains 60,000 training images and 10,000 testing images of handwritten digits from 0-9.',
        owner: {
          address: '0x1234...5678',
          name: 'AI Research Lab'
        },
        price: 0.05,
        category: 'computer-vision',
        tags: ['image', 'classification', 'digits', 'neural-networks'],
        rating: 4.5,
        downloads: 1250,
        size: '11.5 MB',
        lastUpdated: '2023-10-15',
        fileType: 'CSV',
        usability: 9.8
      },
      {
        id: 2,
        name: 'IMDb Reviews Dataset',
        description: 'Movie reviews for sentiment analysis. Contains 50,000 highly polarized reviews from the Internet Movie Database, labeled as positive or negative.',
        owner: {
          address: '0xabcd...efgh',
          name: 'NLP Innovations'
        },
        price: 0.03,
        category: 'nlp',
        tags: ['text', 'sentiment-analysis', 'classification', 'reviews'],
        rating: 4.2,
        downloads: 875,
        size: '80.2 MB',
        lastUpdated: '2023-11-05',
        fileType: 'JSON',
        usability: 8.7
      },
      {
        id: 3,
        name: 'Stock Market Historical Data',
        description: 'Historical stock data with technical indicators for the S&P 500 companies. Includes daily OHLC prices, volume, and calculated indicators like RSI, MACD, and moving averages.',
        owner: {
          address: '0x9876...5432',
          name: 'FinTech Data Solutions'
        },
        price: 0.08,
        category: 'time-series',
        tags: ['finance', 'stocks', 'time-series', 'prediction'],
        rating: 3.9,
        downloads: 620,
        size: '156 MB',
        lastUpdated: '2023-09-28',
        fileType: 'CSV/Parquet',
        usability: 7.9
      },
      {
        id: 4,
        name: 'COVID-19 Global Dataset',
        description: 'Comprehensive COVID-19 data including cases, deaths, recoveries, testing, and vaccination rates by country and region. Updated daily with historical data from January 2020.',
        owner: {
          address: '0xdef0...1234',
          name: 'Global Health Data Consortium'
        },
        price: 0.00,
        category: 'tabular',
        tags: ['health', 'covid', 'pandemic', 'global'],
        rating: 4.8,
        downloads: 3200,
        size: '250 MB',
        lastUpdated: '2023-11-20',
        fileType: 'CSV/JSON',
        usability: 9.5
      },
      {
        id: 5,
        name: 'Urban Sound Classification',
        description: 'Audio dataset for urban sound classification. Contains 8,732 labeled sound excerpts of urban sounds from 10 classes: air conditioner, car horn, children playing, dog bark, drilling, engine idling, gunshot, jackhammer, siren, and street music.',
        owner: {
          address: '0x5678...9abc',
          name: 'Audio AI Research'
        },
        price: 0.06,
        category: 'computer-vision',
        tags: ['audio', 'sound', 'classification', 'urban'],
        rating: 4.1,
        downloads: 580,
        size: '6.8 GB',
        lastUpdated: '2023-08-12',
        fileType: 'WAV/JSON',
        usability: 8.2
      },
      {
        id: 6,
        name: 'E-commerce Customer Behavior',
        description: 'Anonymized customer behavior data from a major e-commerce platform. Includes browsing patterns, purchase history, product interactions, and demographic information.',
        owner: {
          address: '0x2468...1357',
          name: 'Retail Data Analytics'
        },
        price: 0.10,
        category: 'tabular',
        tags: ['e-commerce', 'customer', 'behavior', 'retail'],
        rating: 4.6,
        downloads: 920,
        size: '1.2 GB',
        lastUpdated: '2023-10-30',
        fileType: 'CSV/Parquet',
        usability: 9.0
      }
    ];

    setTimeout(() => {
      setDatasets(mockDatasets);
      setLoading(false);
    }, 1000); // Simulate network delay
  }, []);

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
  };

  // Filter datasets based on category and search term
  const filteredDatasets = datasets.filter(dataset => {
    const matchesFilter = filter === 'all' || dataset.category === filter;
    const matchesSearch =
      dataset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dataset.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dataset.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  // Sort datasets based on selected sort option
  const sortedDatasets = [...filteredDatasets].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.lastUpdated) - new Date(a.lastUpdated);
      case 'oldest':
        return new Date(a.lastUpdated) - new Date(b.lastUpdated);
      case 'downloads':
        return b.downloads - a.downloads;
      case 'rating':
        return b.rating - a.rating;
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      default:
        return 0;
    }
  });

  // Function to render star ratings
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<i key={i} className="fas fa-star"></i>);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<i key={i} className="fas fa-star-half-alt"></i>);
      } else {
        stars.push(<i key={i} className="far fa-star"></i>);
      }
    }

    return <div className="star-rating">{stars} <span className="rating-value">({rating})</span></div>;
  };

  return (
    <div className="marketplace-page">
      <div className="marketplace-header">
        <div className="container">
          <h1>Explore AI Datasets & Models</h1>
          <p>Discover high-quality datasets for machine learning, AI research, and data science projects</p>
        </div>
      </div>

      <div className="container">
        <div className="marketplace-controls">
          <div className="filters-panel">
            <div className="filter-group">
              <label>Category</label>
              <select value={filter} onChange={handleFilterChange} className="form-control">
                <option value="all">All Categories</option>
                <option value="computer-vision">Computer Vision</option>
                <option value="nlp">Natural Language Processing</option>
                <option value="time-series">Time Series</option>
                <option value="tabular">Tabular Data</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={handleSortChange} className="form-control">
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="downloads">Most Downloads</option>
                <option value="rating">Highest Rated</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>

            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => toggleViewMode('grid')}
              >
                <i className="fas fa-th"></i>
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => toggleViewMode('list')}
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
          </div>

          <div className="search-panel">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search datasets by name, description, or tags..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="form-control search-input"
              />
              <button className="search-btn">
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="results-summary">
          <p>Showing {sortedDatasets.length} datasets</p>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading datasets...</p>
          </div>
        ) : (
          <div className={`dataset-container ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
            {sortedDatasets.length > 0 ? (
              sortedDatasets.map(dataset => (
                <div key={dataset.id} className="dataset-card">
                  <div className="dataset-header">
                    <h3 className="dataset-title">
                      <Link to={`/dataset/${dataset.id}`}>{dataset.name}</Link>
                    </h3>
                    <div className="dataset-meta">
                      <span className="dataset-owner">
                        <i className="fas fa-user"></i> {dataset.owner.name}
                      </span>
                      <span className="dataset-updated">
                        <i className="fas fa-calendar-alt"></i> Updated {dataset.lastUpdated}
                      </span>
                    </div>
                  </div>

                  <div className="dataset-body">
                    <p className="dataset-description">{dataset.description}</p>

                    <div className="dataset-tags">
                      {dataset.tags.map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  </div>

                  <div className="dataset-footer">
                    <div className="dataset-stats">
                      <div className="stat">
                        <i className="fas fa-download"></i>
                        <span>{dataset.downloads}</span>
                      </div>
                      <div className="stat">
                        <i className="fas fa-database"></i>
                        <span>{dataset.size}</span>
                      </div>
                      <div className="stat">
                        <i className="fas fa-file"></i>
                        <span>{dataset.fileType}</span>
                      </div>
                    </div>

                    <div className="dataset-rating">
                      {renderStars(dataset.rating)}
                    </div>

                    <div className="dataset-actions">
                      <span className="dataset-price">
                        {dataset.price === 0 ? 'Free' : `${dataset.price} ETH`}
                      </span>
                      <button className="btn btn-primary purchase-btn">
                        {dataset.price === 0 ? 'Download' : 'Purchase'}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <i className="fas fa-search"></i>
                <h3>No datasets found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;
